/**
 * ESCHER FALLS — 正射影と錯視接続の数学。
 * VIEW-EXPANSION-SPEC.md / PROJECTION-9VIEW.md の実装。
 *
 * **純関数のみ。three.js に依存しない。モジュール状態を持たない。**
 * ここが「画面上で重なる ⇔ 接続」という本作の中核判定の唯一の実装元である。
 *
 * ## 姿勢と関係を混同しないこと
 * - {@link ViewPose} は「どこから見ているか」。**12種類**（方位4 × 仰角3）。
 * - {@link ViewRelation} は「何と何が重なるか」。**9種類**。
 *   真上の4姿勢は絵が回るだけで接続は同じなので、すべて関係 `8` に落ちる。
 *
 * 接続判定に渡すのは**必ず関係**である。姿勢を渡すと真上を4つと数えてしまい、
 * 「解ける視点はちょうど1つ」という検証が成立しなくなる。
 */

import type { Elevation, ScreenPos, Vec3, ViewPose, ViewRelation } from './types';

/**
 * 方位 k の水平方向 `h_k = (hx, 0, hz)`。**正規化しない**
 * （成分が ±1 であることを {@link overlapsExact} と {@link directionOf} が前提にする）。
 * 並びは VIEW-EXPANSION-SPEC §2.1: `(1,·,1) → (1,·,-1) → (-1,·,-1) → (-1,·,1)`。
 */
const HORIZONTAL: readonly Vec3[] = [
  [1, 0, 1],
  [1, 0, -1],
  [-1, 0, -1],
  [-1, 0, 1],
];

/**
 * 9関係の方向ベクトル。index が {@link ViewRelation} に対応する。
 * **正規化しない**（y 成分が 1 か 2 の整数であることが {@link overlapsExact} の前提）。
 */
export const RELATION_DIRECTIONS: readonly Vec3[] = [
  [1, 1, 1], //   0: A0
  [1, 1, -1], //  1: A1
  [-1, 1, -1], // 2: A2
  [-1, 1, 1], //  3: A3
  [1, 2, 1], //   4: B0
  [1, 2, -1], //  5: B1
  [-1, 2, -1], // 6: B2
  [-1, 2, 1], //  7: B3
  [0, 1, 0], //   8: T（真上）
];

/** 関係の総数。検証ツールとテストが総当たりに使う。 */
export const RELATION_COUNT = 9;

/** デバッグ表示用の関係名。 */
export const RELATION_NAMES: readonly string[] = [
  'A0', 'A1', 'A2', 'A3', 'B0', 'B1', 'B2', 'B3', 'T',
];

/**
 * 錯視接続とみなす画面距離のしきい値（ワールド単位）。
 *
 * **0.02 を変えてはならない**（PROJECTION-9VIEW §3.7）。
 * 誤接続の下限は関係ごとに違い、最悪は B の `√(1/8) = 0.353553`（安全率 17.7 倍）。
 * `0.08` に戻すと安全率が 4.42 倍に落ちるうえ、遷移中の準一致
 * `d = (1, 1.5, 0.5)`（最接近 0.0468、滞在 20.6ms ＝ 1.2 フレーム）を拾ってしまう。
 *
 * なお**接続判定の正は {@link overlapsExact}** であり、この値は
 * デバッグ表示と「どれくらい惜しいか」の計測にしか使わない。
 */
export const CONNECT_EPS = 0.02;

/** 仰角 index → 水平面からの角度（rad）。A = 35.264°, B = 54.736°, T = 90°。 */
export const ELEVATION_ANGLES: readonly number[] = [
  Math.atan2(1, Math.SQRT2), // A: dir = (1,1,1)/√3
  Math.atan2(2, Math.SQRT2), // B: dir = (1,2,1)/√6
  Math.PI / 2, //              T: dir = (0,1,0)
];

/** 方位 index → 方位角（rad）。`θ = k·π/2`。 */
export function azimuthAngle(k: number): number {
  return k * (Math.PI / 2);
}

/**
 * 姿勢 → 接続関係。**真上（仰角 2）は方位によらず常に 8。**
 * この1行が「12姿勢 / 9関係」という設計の要である。
 */
export function relationOf(pose: ViewPose): ViewRelation {
  return (pose.elevation === 2 ? 8 : pose.elevation * 4 + pose.azimuth) as ViewRelation;
}

/** 姿勢 → カメラ方向ベクトル（正規化前）。`relationOf(pose)` の方向ベクトルと一致する。 */
export function directionOf(pose: ViewPose): Vec3 {
  if (pose.elevation === 2) return [0, 1, 0];
  const h = HORIZONTAL[pose.azimuth];
  return [h[0], pose.elevation === 0 ? 1 : 2, h[2]];
}

/**
 * 姿勢 → カメラの up（単位ベクトル）。
 *
 * A・B は `(0,1,0)` でよいが、真上では `(0,1,0)` が視線と平行になり、
 * **three.js は例外を投げずに視線を 1e-4 rad ずらす**（PROJECTION-9VIEW §3.9.2）。
 * しかも `Math.cos(Math.PI/2)` が 0 でなく 6.12e-17 なので、
 * 1e-16 の差で「正しい」「−Z 固定」「180°反転」の3通りに分岐する。
 *
 * そこで真上では方位 k の水平方向の逆向きを画面の上に取る。
 * こうすると B_k → T_k の遷移で絵の向きが連続する。
 * **up を変えても接続関係は変わらない**（射影方向が同じなら重なる点対は不変。
 * 205通りの up で検証済み）。
 *
 * ここで返すのは「静止時の12姿勢」用。**遷移中は必ず {@link poseFrame} を使うこと。**
 */
export function upOf(pose: ViewPose): Vec3 {
  if (pose.elevation !== 2) return [0, 1, 0];
  const h = HORIZONTAL[pose.azimuth];
  return [-h[0] * Math.SQRT1_2, 0, -h[2] * Math.SQRT1_2];
}

/**
 * 連続な (方位角 θ, 仰角 φ) からカメラのフレームを作る。**遷移中もこれだけを使う。**
 *
 * ```
 * ĥ(θ) = (cosθ + sinθ, 0, cosθ − sinθ)/√2   … θ = k·π/2 で h_k/√2 に一致
 * dir  =  cosφ·ĥ(θ) + sinφ·ŷ                … カメラは center + dir·dist に立つ
 * up   = −sinφ·ĥ(θ) + cosφ·ŷ                … dir と厳密に直交（内積は恒等的に 0）
 * ```
 *
 * `dir ⊥ up` が恒等的に成り立つので、**three.js の `lookAt` が退化経路に落ちることはない**。
 * A・B では `up = (0,1,0)` を渡した場合と同じ基底になる（right の差 1.1e-16）。
 * つまり従来の見た目は1ピクセルも変わらない。
 *
 * 検算: `(0, φ_A)` → dir `(1,1,1)/√3`, up `(-1,2,-1)/√6`（＝従来の camUp）。
 *       `(0, π/2)` → dir `(0,1,0)`, up `(-1,0,-1)/√2` ＝ {@link upOf} と一致。
 */
export function poseFrame(
  theta: number,
  phi: number,
): { dir: Vec3; up: Vec3 } {
  const c = Math.cos(theta);
  const s = Math.sin(theta);
  const hx = (c + s) * Math.SQRT1_2;
  const hz = (c - s) * Math.SQRT1_2;
  const cp = Math.cos(phi);
  const sp = Math.sin(phi);
  return {
    dir: [cp * hx, sp, cp * hz], // 既に単位長
    up: [-sp * hx, cp, -sp * hz], // 既に単位長・dir と直交
  };
}

// =============================================================================
// 接続判定
// =============================================================================

/**
 * 2点がその関係で重なるかの**厳密判定**。除算もイプシロンも使わない。
 * **これが接続判定の正である。**
 *
 * 9関係すべて `vy ≠ 0` なので、`d` が `v` の実数倍なら倍率は必ず `dy/vy`。
 * 両辺に `vy` を掛けて割り算を消してある。座標は `.5` 刻み、`v` の成分は
 * `{-1,0,1,2}` なので積も差も浮動小数で厳密。`===` で足りる。
 *
 * 展開すると
 * - `A_k`: `dx = vx·dy ∧ dz = vz·dy`
 * - `B_k`: `2dx = vx·dy ∧ 2dz = vz·dy`
 * - `T`: `dx = 0 ∧ dz = 0`（高さは自由）
 */
export function overlapsExact(a: Vec3, b: Vec3, rel: ViewRelation): boolean {
  const v = RELATION_DIRECTIONS[rel];
  const dy = b[1] - a[1];
  return (b[0] - a[0]) * v[1] === v[0] * dy && (b[2] - a[2]) * v[1] === v[2] * dy;
}

/**
 * 2点の画面距離（ワールド単位、非負）。関係ごとの二次形式で計算する。
 *
 * `A = B = 0` がちょうど「重なる」に対応するので、一致すべきペアの距離は厳密に 0 になる。
 * 差を先に取ってあるので、座標が原点から遠くても桁落ちしない。
 */
export function screenDistance(a: Vec3, b: Vec3, rel: ViewRelation): number {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const dz = b[2] - a[2];
  if (rel === 8) return Math.hypot(dx, dz); // T: y を完全に潰す
  const v = RELATION_DIRECTIONS[rel];
  if (rel < 4) {
    const A = dx - v[0] * dy; // A_k
    const B = dz - v[2] * dy;
    return Math.sqrt((2 / 3) * (A * A + B * B - v[0] * v[2] * A * B));
  }
  const A = 2 * dx - v[0] * dy; // B_k
  const B = 2 * dz - v[2] * dy;
  return Math.sqrt((1 / 24) * (5 * A * A + 5 * B * B - 2 * v[0] * v[2] * A * B));
}

/**
 * 2点がその関係で重なって見えるか。
 *
 * 既定では {@link overlapsExact} に委譲する（除算もイプシロンも無い厳密判定）。
 * `eps` を明示したときだけ画面距離での判定になる。
 * これはデバッグ表示や「どれくらい惜しいか」の計測用であって、
 * **ゲームの接続判定でイプシロンを使う理由は無い**。
 *
 * 深度は一切見ない。同一ピースかどうかも見ない（`core/graph.ts` の責務）。
 */
export function overlapsInRelation(
  a: Vec3,
  b: Vec3,
  rel: ViewRelation,
  eps?: number,
): boolean {
  if (eps === undefined) return overlapsExact(a, b, rel);
  return screenDistance(a, b, rel) < eps;
}

/**
 * ワールド座標を姿勢の画面座標へ射影する。単位はワールド単位（ピクセルでも NDC でもない）。
 *
 * `right` は方位だけで決まり、`camUp` は {@link poseFrame} の `up` そのものになる
 * （`dir ⊥ up` なので `cross(right, viewDir) = up`）。
 */
export function toScreen(pos: Vec3, pose: ViewPose): ScreenPos {
  const theta = azimuthAngle(pose.azimuth);
  const { up } = poseFrame(theta, ELEVATION_ANGLES[pose.elevation]);
  // right(θ) = ĥ(θ + 90°)。方位だけで決まり仰角に依存しない。
  const c = Math.cos(theta + Math.PI / 2);
  const s = Math.sin(theta + Math.PI / 2);
  const rx = (c + s) * Math.SQRT1_2;
  const rz = (c - s) * Math.SQRT1_2;
  return {
    x: pos[0] * rx + pos[2] * rz,
    y: pos[0] * up[0] + pos[1] * up[1] + pos[2] * up[2],
  };
}

// =============================================================================
// 姿勢の遷移
// =============================================================================

/** 方位を1つ回す。巡回する。純関数。 */
export function rotateAzimuth(pose: ViewPose, dir: -1 | 1): ViewPose {
  return { ...pose, azimuth: (((pose.azimuth + dir + 4) % 4) as ViewPose['azimuth']) };
}

/** 仰角を1段変える。**端では何も起きず同じ姿勢を返す**（巡回しない）。純関数。 */
export function changeElevation(pose: ViewPose, dir: -1 | 1): ViewPose {
  const next = pose.elevation + dir;
  if (next < 0 || next > 2) return pose;
  return { ...pose, elevation: next as Elevation };
}

/** 姿勢が等しいか。 */
export function samePose(a: ViewPose, b: ViewPose): boolean {
  return a.azimuth === b.azimuth && a.elevation === b.elevation;
}

/**
 * 姿勢 `from` から `to` へ移るのに要する最短手数（方位回転＋仰角移動）。
 * 検証ツールが難易度を測るのに使う。
 */
export function poseDistance(from: ViewPose, to: ViewPose): number {
  const dAz = Math.abs(from.azimuth - to.azimuth);
  const azTurns = Math.min(dAz, 4 - dAz);
  const elSteps = Math.abs(from.elevation - to.elevation);
  // 真上へ上がると方位は絵の向きしか変えないので、方位合わせは不要になる。
  if (to.elevation === 2) return elSteps;
  return azTurns + elSteps;
}

/**
 * ある関係に到達できる姿勢のうち、`from` から最も近いものの手数。
 * 真上（関係 8）は4姿勢あるが、どれでも同じ関係なので最短のものを採る。
 */
export function movesToRelation(from: ViewPose, rel: ViewRelation): number {
  let best = Number.POSITIVE_INFINITY;
  for (let elevation = 0; elevation < 3; elevation += 1) {
    for (let azimuth = 0; azimuth < 4; azimuth += 1) {
      const pose = { azimuth, elevation } as ViewPose;
      if (relationOf(pose) !== rel) continue;
      best = Math.min(best, poseDistance(from, pose));
    }
  }
  return best;
}
